import { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Typography, LinearProgress, Stack, Button } from "@mui/material";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import WarningIcon from "@mui/icons-material/Warning";

import { LayoutContainer, LayoutWrapper, MessageModal } from "../components";
import {
  observerCartActive,
  setProductsInCart,
  setSummaryInCart,
  setLastCartState,
} from "../state/actions";
import { ItemProduct } from "./cart/ItemProduct";
import StoreService from "../services/StoreService";
import { STORE_ID_DEFAULT } from "../constants/businessValues";
import { userLoggedSelector } from "../state/selectors/sessionSelectors";
import CartService from "../services/CartService";
import {
  cartActiveSelector,
  productsInCartSelector,
  summaryInCartSelector,
} from "../state/selectors/cartSelectors";
import { getPriceFormated } from "../hooks/usePrice";
import styles from "../styles/cartStyles";

const MAX_ATTEMPS = 10;

export default function Cart() {
  const engineInterval = useRef<any>(null);
  const [apiMsg, setApiMsg] = useState(false);
  const [iconSelected, setIconSelected] = useState(() => WarningIcon);
  const [titleSelected, setTitleSelected] = useState("");
  const [descSelected, setDescSelected] = useState([]);
  const [actionsSelected, setActionsSelected] = useState([]);
  const [counterInactive, setCounterInactive] = useState(0);

  const userLogged = useSelector(userLoggedSelector);
  const productsInCart = useSelector(productsInCartSelector);
  const summaryInCart = useSelector(summaryInCartSelector);
  const cartActive = useSelector(cartActiveSelector);
  const navigation = useNavigate();
  const dispatch = useDispatch();

  const messagesByCodeDoor = [
    {
      /*
      code: 17, //
      icon: () => WarningIcon,
      title: "Falló el pago",
      description: [
        "No se pudo procesar el cobro con tu medio de pago predeterminado.",
        "Cambie de tarjeta",
      ],
      actions: [
        {
          title: "Entendido",
          colorPrimary: true,
          onClick: () => {
            stopInterval();
            setApiMsg(false);
            dispatch(setRetryPayment());
            navigation("/payment/list");
          },
        },
      ],
    */
    },
    {
      code: 15,
      icon: () => WarningIcon,
      title: "Elegí tu medio de pago",
      description: [
        "Podés pagar con una tarjeta o elegir otra opción como MercadoPago.",
      ],
      actions: [
        {
          title: "Elegir medio de pago",
          colorPrimary: true,
          onClick: () => {},
        },
      ],
    },
    {
      /*
      code: 100, //
      icon: () => WarningIcon,
      title: "Pago realizado",
      description: ["El pago fue procesado con éxito"],
      actions: [
        {
          title: "Ver resumen de compra",
          colorPrimary: true,
          onClick: () => {
            stopInterval();
            setApiMsg(false);
            navigation("/summary", { state: localLastCartDetected });
          },
        },
      ],
     */
    },
    {
      code: 111, // Usuario confirmo y no ingresó a la tienda
      icon: () => WarningIcon,
      title: "Usted se ha retirado",
      description: ["Se ha retirado sin productos en la tienda."],
      actions: [
        {
          title: "Entendido",
          colorPrimary: true,
          onClick: () => {
            stopInterval();
            setApiMsg(false);
            navigation("/");
          },
        },
      ],
    },
    {
      code: 12, // multiples grupos en salida
      icon: () => WarningIcon,
      title: "Volver a la tienda",
      description: [
        "Su grupo debe   a la tienda.",
        "Solo puede salir un grupo a la vez.",
      ],
      actions: [
        {
          title: "Entendido",
          colorPrimary: true,
          onClick: () => {
            setApiMsg(false);
          },
        },
      ],
    },
    {
      code: 13, // grupo incompleto
      icon: () => WarningIcon,
      title: "Grupo incompleto",
      description: [
        "Todos los integrantes de tu grupo deben estar en el hall para avanzar con el pago o salir de la tienda.",
      ],
      actions: [
        {
          title: "Entendido",
          colorPrimary: true,
          onClick: () => {
            setApiMsg(false);
          },
        },
      ],
    },
  ];

  // Evaluacion principal que activa el get de productos
  const evaluationCart = useCallback(() => {
    CartService.getOrderActive(userLogged.login)
      .then((response) => {
        if (Array.isArray(response) && response[0]?.length > 0) {
          const [summaryAux, ...rest] = response;
          const summary = summaryAux[0];
          dispatch(observerCartActive(true));
          dispatch(setProductsInCart(rest));
          dispatch(setSummaryInCart(summary));
          dispatch(
            setLastCartState({
              productsInCart: rest,
              summaryInCart: summary,
            })
          );
        } else {
          dispatch(observerCartActive(false));
          if (response?.status === "400") {
            dispatch(setProductsInCart([]));
            dispatch(setSummaryInCart(null));
            setCounterInactive((c) => c + 1);
          }
        }
      })
      .catch((error) => {
        console.error(error);
        dispatch(setProductsInCart([]));
        dispatch(setSummaryInCart(null));
        setCounterInactive((c) => c + 1);
      });
  }, [userLogged, dispatch]);

  const startInterval = () => {
    engineInterval.current = setInterval(() => {
      evaluationCart();
      accessStatus();
    }, 2000);
  };

  const stopInterval = () => {
    if (engineInterval.current) {
      clearInterval(engineInterval.current);
      engineInterval.current = null;
    }
  };

  useEffect(() => {
    return () => stopInterval();
  }, []);

  // FUNCION QUE PIDE MENSAJES CONSTANTEMENTE SOBRE ESTATUS DE LA PUERTA Y PAGO
  const accessStatus = () => {
    StoreService.getAccessStatus(userLogged.login, STORE_ID_DEFAULT).then(
      (response) => {
        if (response instanceof Error) {
          console.log("accessStatus error - ", response);
          return;
        } else if (response.result.length > 0) {
          stopInterval();
          const accessStoreResponse =
            response.result[response.result.length - 1];
          const codeResponse = parseInt(accessStoreResponse.code);
          const redirectMessage = accessStoreResponse.message;

          configMsgByCode(codeResponse, redirectMessage);
        }
      }
    );
  };

  // Funcion para estructurar modal de notificacion y que accione el usuario
  // abrir flujo acompañantes
  const configMsgByCode = (codeResponse: any, message: string) => {
    const elementCode: any = messagesByCodeDoor.find(
      (el) => el.code === codeResponse
    );
    console.log("Codigo: ", codeResponse);

    if (codeResponse === 15) {
      stopInterval();
      setApiMsg(false);
      navigation("/payment/choose", {
        state: {
          redirectUrl: message,
        },
      });
      return;
    }

    if (!elementCode) {
      return;
    }

    setIconSelected(elementCode?.icon);
    setTitleSelected(elementCode?.title);
    setDescSelected(elementCode?.description);
    setActionsSelected(elementCode?.actions);
    setApiMsg(true);
  };

  const cleanMsgCode = () => {
    setIconSelected(() => WarningIcon);
    setTitleSelected("");
    setDescSelected([]);
    setActionsSelected([]);
    setApiMsg(false);
  };

  useEffect(() => {
    if (!userLogged || counterInactive > MAX_ATTEMPS) {
      stopInterval();
      navigation("/");
    }
  }, [userLogged, counterInactive]);

  useEffect(() => {
    if (!apiMsg) {
      cleanMsgCode();
    }
  }, [apiMsg, navigation, userLogged]);

  const setDefaultPaymentAndStartInterval = useCallback(async () => {
    if (userLogged?.login) {
      await stopInterval();
      await startInterval();
    }
  }, [userLogged]);

  useEffect(() => {
    setDefaultPaymentAndStartInterval();
    return () => stopInterval();
  }, []);

  if (!userLogged) {
    return null;
  }

  return (
    <LayoutContainer>
      <LayoutWrapper>
        <>
          {!productsInCart?.length ? (
            <>
              <Stack spacing={3} style={{ alignItems: "center" }}>
                <ShoppingCartRoundedIcon
                  color="primary"
                  style={{ fontSize: "128px" }}
                />
                <Typography
                  variant="h4"
                  color="white"
                  align="center"
                  style={{ fontWeight: "bold" }}
                >
                  {!cartActive
                    ? "No tiene un carrito activo"
                    : "Tu carrito está vacío"}
                </Typography>
                <Typography variant="h6" color="white" align="center">
                  {!cartActive
                    ? "Accede correctamente al hall de ingreso para ser identificado."
                    : "Comienza tu compra agarrando un producto"}
                </Typography>
              </Stack>
              {apiMsg && (
                <div style={styles.contentFit}>
                  <MessageModal
                    type="text"
                    Icon={iconSelected}
                    title={titleSelected}
                    text={descSelected}
                    actions={actionsSelected}
                    handleClose={() => setApiMsg(false)}
                    open={apiMsg}
                  />
                </div>
              )}
            </>
          ) : (
            productsInCart?.map((el: any) => (
              <ItemProduct key={el.barcode} item={el} />
            ))
          )}
        </>

        <LinearProgress variant="determinate" value={100} />

        <div style={styles.contentFooter}>
          <div style={styles.contentSummary}>
            {summaryInCart && cartActive ? (
              <>
                <Typography
                  variant="h5"
                  color="white"
                  style={{ fontWeight: "bold", fontSize: "20px" }}
                >
                  Total:
                </Typography>
                <Typography
                  variant="h4"
                  color="white"
                  style={{ fontWeight: "bold" }}
                >
                  {getPriceFormated(summaryInCart.amount_total)}
                </Typography>
              </>
            ) : null}

            <div style={styles.contentFooterRow}>
              <Button onClick={() => navigation("/")}>VOLVER</Button>
            </div>
          </div>
        </div>

        {apiMsg && (
          <div style={styles.contentFit}>
            <MessageModal
              type="text"
              Icon={iconSelected}
              title={titleSelected}
              text={descSelected}
              actions={actionsSelected}
              handleClose={() => setApiMsg(false)}
              open={apiMsg}
            />
          </div>
        )}
      </LayoutWrapper>
    </LayoutContainer>
  );
}
